/**
 * Real Terrain Metrics Display Component
 * Shows actual SRTM-based fractal analysis, slope, TRI, TTI
 * Replaces generic route comparisons with scientific metrics
 */

import React, { useState } from 'react';
import { Mountain, TrendingUp, Activity, Navigation, AlertTriangle, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import Tooltip from './Tooltip';

const RealTerrainMetrics = ({ analysisData }) => {
    const { getThemeClasses } = useTheme();
    const themeClasses = getThemeClasses();
    
    // State for collapsible sections
    const [showDetailedAnalysis, setShowDetailedAnalysis] = useState(false);
    const [showBoxCounting, setShowBoxCounting] = useState(false);
    
    if (!analysisData) {
        return (
            <div className={`${themeClasses.card} border ${themeClasses.border} rounded-xl p-6`}>
                <p className={themeClasses.textMuted}>No terrain analysis data available</p>
            </div>
        );
    }
    
    const { fractal, slope, roughness, elevation, traversability, metadata } = analysisData;
    
    // Risk level color
    const getRiskColor = (level) => {
        switch(level) {
            case 'High': return 'text-red-400 bg-red-500/20';
            case 'Moderate': return 'text-yellow-400 bg-yellow-500/20';
            case 'Low': return 'text-green-400 bg-green-500/20';
            default: return 'text-gray-400 bg-gray-500/20';
        }
    };
    
    return (
        <div className={`${themeClasses.card} border ${themeClasses.border} rounded-xl p-6 mt-6`}>
            {/* Header with Data Source Badge */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <div className="bg-blue-600 p-2 rounded-lg">
                        <Mountain className="text-white" size={24} />
                    </div>
                    <div>
                        <h3 className={`text-2xl font-bold ${themeClasses.textPrimary}`}>
                            Scientific Terrain Analysis
                        </h3>
                        <p className={`text-sm ${themeClasses.textMuted}`}>
                            SRTM 30m Resolution • {metadata.total_points.toLocaleString()} data points
                        </p>
                        <p className={`text-xs ${themeClasses.textMuted} mt-1`}>
                            📍 Area-wide analysis • Includes terrain surrounding route
                        </p>
                    </div>
                </div>
                <div className="bg-green-500/20 border border-green-500/50 px-4 py-2 rounded-lg">
                    <p className="text-green-400 font-semibold text-sm flex items-center gap-2">
                        <CheckCircle size={16} />
                        REAL DATA
                    </p>
                </div>
            </div>
            
            {/* Primary Metrics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                
                {/* Fractal Dimension Card */}
                <div className={`${themeClasses.secondary} border ${themeClasses.border} rounded-lg p-4`}>
                    <div className="flex items-center gap-2 mb-3">
                        <Activity className="text-purple-400" size={18} />
                        <h5 className={`${themeClasses.textSecondary} font-semibold text-sm`}>Fractal Dimension</h5>
                    </div>
                    <div className="space-y-2">
                        <div className="flex justify-between">
                            <span className={`${themeClasses.textMuted} text-xs flex items-center gap-1`}>
                                D-Value:
                                <Tooltip text="Fractal Dimension (1.0-3.0). Measures terrain complexity using box-counting analysis. Values near 2.0 indicate flat terrain, higher values indicate more complex, mountainous terrain." />
                            </span>
                            <span className="text-purple-400 font-bold text-lg">
                                {fractal.dimension.toFixed(3)}
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className={`${themeClasses.textMuted} text-xs`}>Complexity:</span>
                            <span className={`font-bold ${fractal.complexity_score > 70 ? 'text-red-400' : fractal.complexity_score > 40 ? 'text-yellow-400' : 'text-green-400'}`}>
                                {Math.round(fractal.complexity_score)}/100
                            </span>
                        </div>
                        <div className="mt-2 pt-2 border-t border-gray-700/30">
                            <p className="text-xs text-purple-300">
                                {fractal.interpretation}
                            </p>
                            {fractal.dimension_note && (
                                <p className="text-xs text-yellow-400 mt-2 flex items-start gap-1">
                                    <span>⚠️</span>
                                    <span>{fractal.dimension_note}</span>
                                </p>
                            )}
                        </div>
                    </div>
                </div>
                
                {/* Slope Analysis Card */}
                <div className={`${themeClasses.secondary} border ${themeClasses.border} rounded-lg p-4`}>
                    <div className="flex items-center gap-2 mb-3">
                        <TrendingUp className="text-orange-400" size={18} />
                        <h5 className={`${themeClasses.textSecondary} font-semibold text-sm`}>Slope Analysis</h5>
                    </div>
                    <div className="space-y-2">
                        <div className="flex justify-between">
                            <span className={`${themeClasses.textMuted} text-xs`}>Max Slope:</span>
                            <span className={`font-bold ${slope.max_slope_degrees > 25 ? 'text-red-400' : slope.max_slope_degrees > 15 ? 'text-orange-400' : 'text-green-400'}`}>
                                {slope.max_slope_degrees.toFixed(1)}°
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className={`${themeClasses.textMuted} text-xs`}>Avg Slope:</span>
                            <span className="text-yellow-400 font-bold">
                                {slope.avg_slope_degrees.toFixed(1)}°
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className={`${themeClasses.textMuted} text-xs flex items-center gap-1`}>
                                Steep Sections:
                                <Tooltip text="Percentage of area with slopes greater than 15° (27% grade). Higher values indicate more challenging terrain requiring low gear and careful driving." />
                            </span>
                            <span className="text-orange-400 font-bold">
                                {slope.steep_sections_percent.toFixed(1)}%
                            </span>
                        </div>
                        <div className="mt-2 pt-2 border-t border-gray-700/30">
                            <div className="text-xs text-orange-300 flex items-center gap-1">
                                {slope.grade_classification}
                                <Tooltip text="Classification of maximum slope steepness. Ranges from Flat (<3°) to Extreme (>25°). Determines vehicle capability requirements and safety considerations." />
                            </div>
                        </div>
                    </div>
                </div>
                
                {/* Terrain Ruggedness Card */}
                <div className={`${themeClasses.secondary} border ${themeClasses.border} rounded-lg p-4`}>
                    <div className="flex items-center gap-2 mb-3">
                        <Mountain className="text-cyan-400" size={18} />
                        <h5 className={`${themeClasses.textSecondary} font-semibold text-sm`}>Terrain Ruggedness</h5>
                    </div>
                    <div className="space-y-2">
                        <div className="flex justify-between">
                            <span className={`${themeClasses.textMuted} text-xs flex items-center gap-1`}>
                                TRI Score:
                                <Tooltip text="Terrain Ruggedness Index (meters). Measures elevation variation between adjacent cells. Higher values indicate more rugged, uneven terrain with greater elevation changes." />
                            </span>
                            <span className="text-cyan-400 font-bold text-lg">
                                {roughness.terrain_ruggedness_index.toFixed(2)}
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className={`${themeClasses.textMuted} text-xs`}>Roughness:</span>
                            <span className="text-blue-400 font-bold">
                                {roughness.surface_roughness_m.toFixed(3)}m
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className={`${themeClasses.textMuted} text-xs`}>Curvature:</span>
                            <span className="text-indigo-400 font-bold">
                                {(roughness.curvature_index * 100).toFixed(2)}
                            </span>
                        </div>
                        <div className="mt-2 pt-2 border-t border-gray-700/30">
                            <p className="text-xs text-cyan-300">
                                {roughness.interpretation}
                            </p>
                        </div>
                    </div>
                </div>
                
                {/* Traversability Card */}
                <div className={`${themeClasses.secondary} border ${themeClasses.border} rounded-lg p-4`}>
                    <div className="flex items-center gap-2 mb-3">
                        <Navigation className="text-green-400" size={18} />
                        <h5 className={`${themeClasses.textSecondary} font-semibold text-sm`}>Traversability</h5>
                    </div>
                    <div className="space-y-2">
                        <div className="flex justify-between">
                            <span className={`${themeClasses.textMuted} text-xs flex items-center gap-1`}>
                                TTI Score:
                                <Tooltip text="Terrain Traversability Index (0-100). Combines slope, roughness, and complexity to assess how easily vehicles can traverse the area. Higher scores indicate easier travel." />
                            </span>
                            <span className={`font-bold ${traversability.tti_score >= 80 ? 'text-green-400' : traversability.tti_score >= 60 ? 'text-yellow-400' : 'text-red-400'}`}>
                                {traversability.tti_score}/100
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className={`${themeClasses.textMuted} text-xs`}>Accessibility:</span>
                            <span className="text-green-400 font-bold">
                                {traversability.accessibility_score}/100
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className={`${themeClasses.textMuted} text-xs`}>Risk Level:</span>
                            <span className={`font-bold px-2 py-1 rounded text-xs ${getRiskColor(traversability.risk_level.level)}`}>
                                {traversability.risk_level.level}
                            </span>
                        </div>
                        <div className="mt-2 pt-2 border-t border-gray-700/30">
                            <p className="text-xs text-green-300">
                                Score: {traversability.risk_level.score}/100
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Collapsible Button for Detailed Analysis */}
            <div className="mt-6">
                <button
                    onClick={() => setShowDetailedAnalysis(!showDetailedAnalysis)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-800/30 hover:bg-blue-800/50 border border-blue-600/50 rounded-lg transition-colors text-blue-300 text-sm font-medium"
                >
                    {showDetailedAnalysis ? (
                        <>
                            <ChevronUp size={18} />
                            Hide Detailed Analysis
                        </>
                    ) : (
                        <>
                            <ChevronDown size={18} />
                            Show Detailed Analysis
                        </>
                    )}
                </button>
            </div>

            {/* Collapsible Detailed Analysis Section */}
            {showDetailedAnalysis && (
                <div className="mt-6 space-y-6">
                    {/* Detailed Elevation Statistics */}
                    <div className={`${themeClasses.secondary} border ${themeClasses.border} rounded-lg p-4`}>
                        <h5 className={`${themeClasses.textPrimary} font-semibold mb-4 flex items-center gap-2`}>
                            <Mountain className="text-blue-400" size={18} />
                            Elevation Profile (SRTM-30m)
                        </h5>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                            <div>
                                <p className={`${themeClasses.textMuted} text-xs mb-1`}>Minimum</p>
                                <p className={`${themeClasses.textPrimary} font-bold text-lg`}>{Math.round(elevation.min_m)}m</p>
                            </div>
                            <div>
                                <p className={`${themeClasses.textMuted} text-xs mb-1`}>Maximum</p>
                                <p className={`${themeClasses.textPrimary} font-bold text-lg`}>{Math.round(elevation.max_m)}m</p>
                            </div>
                            <div>
                                <p className={`${themeClasses.textMuted} text-xs mb-1`}>Range</p>
                                <p className="text-orange-400 font-bold text-lg">{Math.round(elevation.range_m)}m</p>
                            </div>
                            <div>
                                <p className={`${themeClasses.textMuted} text-xs mb-1`}>Mean</p>
                                <p className="text-cyan-400 font-bold text-lg">{Math.round(elevation.mean_m)}m</p>
                            </div>
                            <div>
                                <p className={`${themeClasses.textMuted} text-xs mb-1`}>Std Dev</p>
                                <p className="text-purple-400 font-bold text-lg">{Math.round(elevation.std_dev_m)}m</p>
                            </div>
                        </div>
                    </div>
                    
                    {/* Nested Collapsible: Box-Counting Analysis */}
                    <div className={`${themeClasses.secondary} border ${themeClasses.border} rounded-lg p-4`}>
                        <div className="flex items-center justify-between mb-3">
                            <h5 className={`${themeClasses.textPrimary} font-semibold flex items-center gap-2`}>
                                <Activity className="text-purple-400" size={18} />
                                Fractal Box-Counting Analysis (SOO Objective 1)
                            </h5>
                        </div>
                        
                        <button
                            onClick={() => setShowBoxCounting(!showBoxCounting)}
                            className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-purple-800/30 hover:bg-purple-800/50 border border-purple-600/50 rounded-lg transition-colors text-purple-300 text-xs font-medium mb-3"
                        >
                            {showBoxCounting ? (
                                <>
                                    <ChevronUp size={14} />
                                    Hide Box-Counting Data
                                </>
                            ) : (
                                <>
                                    <ChevronDown size={14} />
                                    Show Box-Counting Data
                                </>
                            )}
                        </button>

                        {showBoxCounting && (
                            <div className="mt-3 p-4 bg-purple-950/50 border border-purple-600/30 rounded-lg">
                                <div className="flex gap-1.5 mb-4 w-full">
                                    {fractal.box_counting_data.box_sizes.map((size, idx) => (
                                        <div key={idx} className={`${themeClasses.card} border ${themeClasses.border} rounded px-1.5 py-1 text-center flex-1 min-w-0`}>
                                            <p className={`${themeClasses.textMuted} text-xs mb-0.5 whitespace-nowrap`}>{size}×{size}</p>
                                            <p className="text-purple-400 font-bold text-xs">
                                                {fractal.box_counting_data.counts[idx]}
                                            </p>
                                            <p className={`${themeClasses.textMuted} text-xs`}>boxes</p>
                                        </div>
                                    ))}
                                </div>
                                <div className="flex items-center gap-6 text-xs">
                                    <div className="flex-1">
                                        <p className={`${themeClasses.textMuted} mb-1`}>
                                            <strong>Algorithm:</strong> Multi-scale box-counting (Mandelbrot 1983)
                                        </p>
                                        <p className={`${themeClasses.textMuted}`}>
                                            <strong>Fractal Dimension:</strong> <span className="text-purple-400 font-mono">{fractal.dimension.toFixed(4)}</span>
                                        </p>
                                    </div>
                                    <div className="flex-1">
                                        <p className={`${themeClasses.textMuted} mb-1`}>
                                            <strong>Data Source:</strong> NASA SRTM via OpenTopography
                                        </p>
                                        <p className={`${themeClasses.textMuted}`}>
                                            <strong>Resolution:</strong> {metadata.resolution_m}m ({metadata.grid_size} grid)
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    
                    {/* Operational Assessment */}
                    <div>
                        <h5 className={`${themeClasses.textPrimary} font-semibold mb-4 flex items-center gap-2`}>
                            <AlertTriangle className="text-yellow-400" size={18} />
                            Operational Assessment
                        </h5>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className={`${themeClasses.card} border ${themeClasses.border} rounded-lg p-4`}>
                                <h6 className="text-green-400 font-semibold mb-2 text-sm">Traversability (TTI)</h6>
                                <div className="flex items-center gap-3">
                                    <div className="flex-1">
                                        <div className="relative pt-1">
                                            <div className="overflow-hidden h-2 text-xs flex rounded bg-gray-700">
                                                <div
                                                    style={{ width: `${traversability.tti_score}%` }}
                                                    className={`shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center ${traversability.tti_score >= 80 ? 'bg-green-500' : traversability.tti_score >= 60 ? 'bg-yellow-500' : 'bg-red-500'}`}
                                                ></div>
                                            </div>
                                        </div>
                                    </div>
                                    <span className="text-green-400 font-bold text-2xl">{traversability.tti_score}</span>
                                </div>
                                <p className={`${themeClasses.textMuted} text-xs mt-2`}>
                                    {traversability.tti_score >= 80 ? 'Excellent for operations' : traversability.tti_score >= 60 ? 'Suitable for operations' : 'Challenging terrain'}
                                </p>
                            </div>
                            
                            <div className={`${themeClasses.card} border ${themeClasses.border} rounded-lg p-4`}>
                                <h6 className="text-blue-400 font-semibold mb-2 text-sm">Accessibility</h6>
                                <div className="flex items-center gap-3">
                                    <div className="flex-1">
                                        <div className="relative pt-1">
                                            <div className="overflow-hidden h-2 text-xs flex rounded bg-gray-700">
                                                <div
                                                    style={{ width: `${traversability.accessibility_score}%` }}
                                                    className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-blue-500"
                                                ></div>
                                            </div>
                                        </div>
                                    </div>
                                    <span className="text-blue-400 font-bold text-2xl">{traversability.accessibility_score}</span>
                                </div>
                                <p className={`${themeClasses.textMuted} text-xs mt-2`}>
                                    Based on slope and roughness metrics
                                </p>
                            </div>
                            
                            <div className={`${themeClasses.card} border ${themeClasses.border} rounded-lg p-4`}>
                                <h6 className={`font-semibold mb-2 text-sm ${traversability.risk_level.color === 'red' ? 'text-red-400' : traversability.risk_level.color === 'yellow' ? 'text-yellow-400' : 'text-green-400'}`}>
                                    Operational Risk
                                </h6>
                                <div className="flex items-center justify-between">
                                    <span className={`px-4 py-2 rounded-lg font-bold text-lg ${getRiskColor(traversability.risk_level.level)}`}>
                                        {traversability.risk_level.level}
                                    </span>
                                    <div className="text-right">
                                        <p className={`${themeClasses.textMuted} text-xs`}>Risk Score</p>
                                        <p className={`font-bold text-2xl ${getRiskColor(traversability.risk_level.level)}`}>
                                            {traversability.risk_level.score}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Analysis Scope Notice */}
                    <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                            <AlertTriangle className="text-blue-400 flex-shrink-0 mt-0.5" size={18} />
                            <div>
                                <p className="text-blue-400 font-semibold text-sm mb-1">Analysis Scope</p>
                                <p className={`${themeClasses.textMuted} text-xs`}>
                                    This analysis covers the <strong>entire area</strong> around your route ({metadata.grid_size} grid). 
                                    Elevation and slope values may include terrain features <strong>not on your actual route</strong>. 
                                    For route-specific conditions, refer to the "Route Terrain Analysis" section above.
                                </p>
                            </div>
                        </div>
                    </div>
                    

                </div>
            )}
        </div>
    );
};

export default RealTerrainMetrics;

